import RightTimeApi from "@/API";
import { resolveApiBaseUrl } from "@/utils/apiBackend";

/** Extract filename from Content-Disposition, with a safe fallback. */
export function filenameFromContentDisposition(
  response: Response | { headers?: { get?: (name: string) => string | null } } | null | undefined,
  fallbackFilename: string
): string {
  const contentDisposition = response?.headers?.get?.("Content-Disposition");
  if (!contentDisposition) return fallbackFilename;

  const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(contentDisposition);
  if (!match?.[1]) return fallbackFilename;

  try {
    return decodeURIComponent(match[1].replace(/"/g, "").trim());
  } catch {
    return match[1].replace(/"/g, "").trim() || fallbackFilename;
  }
}

/** Trigger a browser Downloads-folder save without navigating away from the page. */
export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}

/**
 * Normalize an API path or absolute API URL to a path suitable for RightTimeApi.getAsync.
 * External (non-API) absolute URLs are returned unchanged.
 */
export function toApiDownloadPath(urlOrPath: string): string {
  if (!urlOrPath) return urlOrPath;
  if (!urlOrPath.startsWith("http://") && !urlOrPath.startsWith("https://")) {
    return urlOrPath.startsWith("/") ? urlOrPath : `/${urlOrPath}`;
  }

  const apiBase = resolveApiBaseUrl();
  if (apiBase && urlOrPath.startsWith(apiBase)) {
    const rest = urlOrPath.slice(apiBase.length);
    return rest.startsWith("/") ? rest : `/${rest}`;
  }

  return urlOrPath;
}

export type AuthenticatedDownloadPayload = {
  blob: Blob;
  filename: string;
};

type DirectoryPickerWindow = Window & {
  showDirectoryPicker?: (options?: {
    mode?: "read" | "readwrite";
  }) => Promise<FileSystemDirectoryHandle>;
};

/** GET an authenticated API path and return the blob + resolved filename (no save). */
export async function fetchAuthenticatedDownload(
  apiPathOrUrl: string,
  fallbackFilename: string
): Promise<AuthenticatedDownloadPayload | null> {
  const path = toApiDownloadPath(apiPathOrUrl);
  const resp = await RightTimeApi.getAsync(path);
  if (!resp?.ok) return null;

  const blob = await resp.blob();
  if (!blob || blob.size === 0) return null;

  return {
    blob,
    filename: filenameFromContentDisposition(resp, fallbackFilename),
  };
}

/**
 * Prompt once for a destination folder (File System Access API).
 * Returns null when unavailable, cancelled, or denied.
 */
export async function pickSaveDirectory(): Promise<FileSystemDirectoryHandle | null> {
  const w = window as DirectoryPickerWindow;
  if (typeof w.showDirectoryPicker !== "function") return null;

  try {
    return await w.showDirectoryPicker({ mode: "readwrite" });
  } catch (err) {
    if (err && typeof err === "object" && "name" in err && (err as { name?: string }).name === "AbortError") {
      return null;
    }
    return null;
  }
}

/** Write a blob into a previously picked directory using the given filename. */
export async function writeBlobToDirectory(
  directory: FileSystemDirectoryHandle,
  filename: string,
  blob: Blob
): Promise<boolean> {
  try {
    const fileHandle = await directory.getFileHandle(filename, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
    return true;
  } catch {
    return false;
  }
}

export type DirectoryDownloadItem = {
  apiPathOrUrl: string;
  fallbackFilename: string;
};

export type DirectoryDownloadResult = {
  /** User cancelled the folder picker, or the API is unavailable and no fallback ran. */
  cancelled: boolean;
  /** Folder picker unavailable; caller should use per-file browser downloads. */
  unsupported: boolean;
  downloadedCount: number;
  failedIds: number[];
};

/**
 * Prompt for one destination folder, then write each authenticated download into it.
 * Call this while still in a user-gesture turn (before long awaits) so the picker can open.
 */
export async function downloadAuthenticatedFilesToDirectory(
  items: DirectoryDownloadItem[],
  options?: { onItemFailed?: (index: number) => void }
): Promise<DirectoryDownloadResult> {
  if (!items.length) {
    return { cancelled: true, unsupported: false, downloadedCount: 0, failedIds: [] };
  }

  const directory = await pickSaveDirectory();
  if (!directory) {
    const w = window as DirectoryPickerWindow;
    const unsupported = typeof w.showDirectoryPicker !== "function";
    return { cancelled: !unsupported, unsupported, downloadedCount: 0, failedIds: [] };
  }

  let downloadedCount = 0;
  const failedIds: number[] = [];

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const payload = await fetchAuthenticatedDownload(item.apiPathOrUrl, item.fallbackFilename);
    if (!payload) {
      failedIds.push(i);
      options?.onItemFailed?.(i);
      continue;
    }

    const ok = await writeBlobToDirectory(directory, payload.filename, payload.blob);
    if (!ok) {
      failedIds.push(i);
      options?.onItemFailed?.(i);
      continue;
    }
    downloadedCount += 1;
  }

  return { cancelled: false, unsupported: false, downloadedCount, failedIds };
}

/**
 * GET an authenticated API path (or absolute API URL) and trigger a browser file download
 * from the response body. Prefer this over window.open(apiUrl) — Bearer tokens live in
 * sessionStorage and are not sent on a bare navigation to the API origin.
 */
export async function downloadAuthenticatedFile(
  apiPathOrUrl: string,
  fallbackFilename: string
): Promise<boolean> {
  const payload = await fetchAuthenticatedDownload(apiPathOrUrl, fallbackFilename);
  if (!payload) return false;
  triggerBrowserDownload(payload.blob, payload.filename);
  return true;
}

/**
 * Save an already-fetched Response as a file download (GET or POST).
 * Stays on the current page.
 */
export async function downloadResponseAsFile(
  response: Response,
  fallbackFilename: string
): Promise<boolean> {
  if (!response?.ok) return false;
  const blob = await response.blob();
  if (!blob || blob.size === 0) return false;
  triggerBrowserDownload(blob, filenameFromContentDisposition(response, fallbackFilename));
  return true;
}
